import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Checkbox,
  Collapse,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { MdContentCopy, MdExpandLess, MdExpandMore } from 'react-icons/md';
import copy from 'copy-to-clipboard';
import { z } from 'zod';

import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Switch from 'components/Common/Switch';
import { useCreateWebhook, useGetWebhook, useGetWebhookEvents, useUpdateWebhook } from '@/apis/agency/webhooks/hook';
import WebhookFormSkeleton from './WebhookFormSkeleton';

const FALLBACK_EVENTS_BY_SCOPE: Record<string, string[]> = {
  agency: ['agency.package.created', 'agency.package.updated'],
  business: ['business.agent.created', 'business.plan.changed', 'business.usage.overage']
};

const SCOPE_LABELS: Record<string, string> = {
  agency: 'Agency',
  business: 'Business'
};

const eventLabel = (scope: string, fullEvent: string) => {
  const prefix = `${scope}.`;
  return fullEvent.startsWith(prefix) ? fullEvent.slice(prefix.length) : fullEvent;
};

const webhookFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  endpoint_url: z.string().min(1, 'Endpoint URL is required').url('Enter a valid http(s) URL'),
  status: z.boolean(),
  secret: z.string().optional(),
  events: z.array(z.string()).min(1, 'Select at least one event')
});

type WebhookFormValues = z.infer<typeof webhookFormSchema>;

const EMPTY_FORM_VALUES: WebhookFormValues = {
  name: '',
  description: '',
  endpoint_url: '',
  status: true,
  secret: '',
  events: ['business.agent.created']
};

function isNumericWebhookId(webhookId: string | undefined): webhookId is string {
  return Boolean(webhookId && /^\d+$/.test(webhookId));
}

function CopySecretButton({ secret }: { secret: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'} open={copied || undefined} onClose={() => setCopied(false)}>
      <IconButton
        size='small'
        edge='end'
        aria-label='Copy webhook secret'
        onClick={() => {
          copy(secret);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        <MdContentCopy size={16} />
      </IconButton>
    </Tooltip>
  );
}

function EventSampleToggle({ open, onToggle, disabled }: { open: boolean; onToggle: () => void; disabled?: boolean }) {
  return (
    <MuiButton
      size='small'
      variant='text'
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      endIcon={open ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
      sx={{
        textTransform: 'none',
        fontSize: 12,
        fontWeight: 500,
        minWidth: 0,
        px: 0.5,
        py: 0,
        ml: 'auto',
        flexShrink: 0,
        color: 'primary.main',
        whiteSpace: 'nowrap'
      }}
    >
      {open ? 'Hide sample' : 'View sample'}
    </MuiButton>
  );
}

function EventSampleBody({ event, sample }: { event: string; sample: Record<string, unknown> }) {
  const [copied, setCopied] = useState(false);
  const jsonText = JSON.stringify(sample, null, 2);

  return (
    <Box pl={3.5} pb={0.75} pr={0.5}>
      <Box mt={0.25} p={1.25} border='1px solid #E5E7EB' borderRadius={1} bgcolor='#F7F8FA'>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={0.75}>
          <Typography fontSize={11} color='text.secondary'>
            Sample request payload POSTs for{' '}
            <Box component='span' sx={{ fontFamily: 'monospace' }}>
              {event}
            </Box>
          </Typography>
          <Tooltip title={copied ? 'Copied' : 'Copy JSON'} open={copied || undefined} onClose={() => setCopied(false)}>
            <IconButton
              size='small'
              aria-label='Copy sample JSON'
              onClick={() => {
                copy(jsonText);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              <MdContentCopy size={14} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          component='pre'
          sx={{
            m: 0,
            p: 0,
            maxHeight: 220,
            overflow: 'auto',
            fontSize: 11,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {jsonText}
        </Box>
      </Box>
    </Box>
  );
}

function EventRow({
  event,
  scope,
  checked,
  sample,
  onToggleEvent
}: {
  event: string;
  scope: string;
  checked: boolean;
  sample?: Record<string, unknown>;
  onToggleEvent: () => void;
}) {
  const [sampleOpen, setSampleOpen] = useState(false);

  return (
    <Box>
      <Box display='flex' alignItems='center' gap={0.5} py={0.25} pl={0.5} pr={0.5}>
        <FormControlLabel
          sx={{ display: 'flex', m: 0, flex: 1, minWidth: 0, alignItems: 'center' }}
          control={<Checkbox size='small' checked={checked} onChange={onToggleEvent} />}
          label={
            <Tooltip title={event}>
              <Typography fontSize={13} sx={{ textTransform: 'none' }}>
                {eventLabel(scope, event)}
              </Typography>
            </Tooltip>
          }
        />
        <EventSampleToggle open={sampleOpen} disabled={!sample} onToggle={() => setSampleOpen((prev) => !prev)} />
      </Box>
      {sample && (
        <Collapse in={sampleOpen}>
          <EventSampleBody event={event} sample={sample} />
        </Collapse>
      )}
    </Box>
  );
}

export default function WebhookForm() {
  const { webhookId } = useParams();
  const navigate = useNavigate();
  const isEdit = isNumericWebhookId(webhookId);

  const { data: webhook, isLoading } = useGetWebhook();
  const { data: eventsData, isLoading: eventsLoading } = useGetWebhookEvents();
  const { mutate: createWebhook, isPending: creating } = useCreateWebhook();
  const { mutate: updateWebhook, isPending: updating } = useUpdateWebhook();

  const eventsByScope = useMemo(() => {
    const fromApi = eventsData?.data?.events_by_scope;
    if (fromApi && Object.keys(fromApi).length > 0) return fromApi;
    return FALLBACK_EVENTS_BY_SCOPE;
  }, [eventsData]);

  const samples = eventsData?.data?.samples ?? {};
  const availableEvents = useMemo(() => Object.values(eventsByScope).flat(), [eventsByScope]);

  const { handleSubmit, control, reset } = useForm<WebhookFormValues>({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: EMPTY_FORM_VALUES
  });

  useEffect(() => {
    if (!isEdit) {
      reset(EMPTY_FORM_VALUES);
      return;
    }
    if (!webhook) return;
    reset({
      name: webhook.name,
      description: webhook.description || '',
      endpoint_url: webhook.endpoint_url,
      status: webhook.status,
      secret: webhook.secret || '',
      events: (webhook.events || []).filter((event) => availableEvents.includes(event))
    });
  }, [isEdit, webhook, reset, availableEvents]);

  const onSubmit = (data: WebhookFormValues) => {
    const payload = {
      webhook: {
        name: data.name,
        description: data.description || '',
        endpoint_url: data.endpoint_url,
        status: data.status,
        events: data.events.filter((event) => availableEvents.includes(event))
      }
    };

    if (isEdit) {
      updateWebhook({ id: Number(webhookId), payload }, { onSuccess: () => navigate(-1) });
      return;
    }

    createWebhook(payload, { onSuccess: () => navigate(-1) });
  };

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 440, md: 520 },
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'visible'
        }
      }}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' px={3} pt={3} pb={0}>
        <Typography variant='h6' fontWeight={500}>
          {isEdit ? 'Edit webhook' : 'Create webhook'}
        </Typography>
        <IconButton onClick={() => navigate(-1)}>
          <IoClose />
        </IconButton>
      </Box>

      {isEdit && isLoading ? (
        <WebhookFormSkeleton />
      ) : (
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          mt={2}
          flexDirection='column'
          flex={1}
          minHeight={0}
        >
          <Box flex={1} overflow='auto' px={3} pt={1.5} pb={2}>
            <Stack gap={2.5}>
              <Input name='name' label='Name' control={control} fullWidth required />
              <Input name='description' label='Description' control={control} fullWidth multiline minRows={2} />
              <Input
                name='endpoint_url'
                label='Endpoint URL'
                control={control}
                fullWidth
                required
                placeholder='https://example.com/webhooks/kickcall'
              />

              {isEdit && (
                <Input
                  name='secret'
                  label='Webhook Secret'
                  control={control}
                  fullWidth
                  disabled
                  InputProps={{
                    endAdornment: webhook?.secret ? (
                      <InputAdornment position='end'>
                        <CopySecretButton secret={webhook.secret} />
                      </InputAdornment>
                    ) : undefined
                  }}
                  helperText='When your endpoint receives a webhook, verify the X-Webhook-Secret header matches this value.'
                />
              )}

              <Box display='flex' alignItems='center' justifyContent='space-between'>
                <Typography fontSize={14}>Active</Typography>
                <Switch name='status' control={control} />
              </Box>

              <Box>
                <Typography fontSize={14} fontWeight={500} mb={1}>
                  Events
                </Typography>
                <Controller
                  name='events'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Box>
                      <Box
                        sx={{
                          maxHeight: 360,
                          overflowY: 'auto',
                          border: '1px solid #E5E7EB',
                          borderRadius: 1,
                          p: 1
                        }}
                      >
                        {eventsLoading ? (
                          <Typography fontSize={13} color='text.secondary' py={1} px={0.5}>
                            Loading events…
                          </Typography>
                        ) : (
                          Object.entries(eventsByScope).map(([scope, events]) => (
                            <Box key={scope} mb={1.5}>
                              <Typography
                                fontSize={12}
                                fontWeight={600}
                                color='text.secondary'
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.4, px: 0.5, pt: 0.75 }}
                              >
                                {SCOPE_LABELS[scope] || scope}
                              </Typography>
                              <Typography
                                fontSize={11}
                                color='text.secondary'
                                sx={{ px: 0.5, pb: 0.75, lineHeight: 1.4 }}
                              >
                                Event name will be like{' '}
                                <Box component='span' sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                  {events[0] || `${scope}.event.name`}
                                </Box>
                              </Typography>
                              {events.map((event) => {
                                const checked = field.value.includes(event);
                                return (
                                  <EventRow
                                    key={event}
                                    event={event}
                                    scope={scope}
                                    checked={checked}
                                    sample={samples[event]}
                                    onToggleEvent={() => {
                                      field.onChange(
                                        checked ? field.value.filter((item) => item !== event) : [...field.value, event]
                                      );
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          ))
                        )}
                      </Box>
                      {fieldState.error && (
                        <Typography color='error' fontSize={12} mt={0.5}>
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Stack>
          </Box>

          <Box
            display='flex'
            gap={1.5}
            justifyContent='flex-end'
            px={3}
            py={2}
            sx={{
              borderTop: '1px solid #EAEAEA',
              backgroundColor: 'background.paper',
              position: 'sticky',
              bottom: 0,
              mt: 'auto'
            }}
          >
            <Button variant='outlined' onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' isPending={creating || updating}>
              {isEdit ? 'Save changes' : 'Create webhook'}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
